import { get } from 'svelte/store';

import { devices_config } from '@devices/index';
import { devices } from 'src/stores/lists/devices';

import { replaceInterfaceDeviceData } from './add_delete_helpers';
import { getSettings, getStatus } from './get_status_settings_helpers';
import {
	deleteDeviceFromGroupOnModeChange,
	isCover,
	isProCover,
	isProThreeEM,
	isRgbwwPm,
	isShutter,
	is_gen_2,
	real_id,
} from './interface_helpers';

import type { Device } from '../types/Device';
import { DECIMAL_PRECISION_MIN } from './virtual_helpers/virtual_components_status_helpers';

/**
 * Builds replace data for device mode replacement
 * @param device - The device object
 * @param mode - The new mode
 * @param propToChange - Property to change ('category' or 'mode')
 * @param isMultiChannel - Whether to create multiple channels
 * @returns Replace data object
 */
function buildReplaceData(
	device: Device,
	mode: string,
	propToChange: string,
	isMultiChannel: boolean = false,
): Record<string, any> {
	const replaceData = {};
	const channelsCount =
		devices_config[device.type]?.modes?.[mode]?.channels || (isMultiChannel ? 2 : 1);

	for (let i = 0; i < channelsCount; i++) {
		const deviceId = i === 0 ? real_id(device.id) : `${real_id(device.id)}_${i}`;
		const deviceName = i === 0 ? device.name : `${i + 1}) ${device.name}`;

		const modeToSave = isRgbwwPm(device.type) ? getProRGBWWDeviceMode(mode, i) : mode;

		const deviceData = {
			...device,
			id: deviceId,
			name: deviceName,
			[propToChange]: modeToSave,
			mode: modeToSave,
			channel: i,
			channels_count: channelsCount,
			template: `${device.type}-${mode}`,
			mainActionType: null,
			statusLabelParam: null,
			virtualDecimalPrecision: DECIMAL_PRECISION_MIN,
		};

		// Add position adjustment for multi-channel devices (except first channel)
		if (isMultiChannel && i > 0) {
			deviceData.position = device.position + 1;
		}

		// Special handling for Pro RGBWW PM
		if (isRgbwwPm(device.type)) {
			deviceData.rgbww_profile = mode;
		}

		replaceData[deviceId] = deviceData;
	}

	return replaceData;
}

/**
 * Executes device mode replacement using the new endpoint
 * @param device - The device object
 * @param deviceId - The device ID
 * @param mode - The new mode
 * @param propToChange - Property to change ('category' or 'mode')
 * @param isMultiChannel - Whether to create multiple channels
 */
export async function executeModeReplacement(
	device: Device,
	mode: string,
	propToChange: string,
	isMultiChannel: boolean = false,
): Promise<void> {
	const replaceData = buildReplaceData(device, mode, propToChange, isMultiChannel);

	await replaceInterfaceDeviceData(device.id, device.channels_count || 1, replaceData);
	await getStatus(device);
	await getSettings(device);
}
//#endregion

//#region Mode switch logic
/**
 * Checks in very early stage if there are new settings coming from local web, bluetooth, etc.
 * @param id
 * @param settings
 * @param initialLoad: boolean to indicate if this is the initial load of the device - for covers this is used to prevent unnecessary mode changes and group deletions
 * @returns
 */
export async function checkMode(id, settings, initialLoad = false) {
	if (typeof settings === 'undefined') return;

	const device = get(devices)[id];

	let isShared = device?.shared;

	if (typeof device === 'undefined') return;

	if (device.type === 'SNDM-0013US') return;

	let settings_mode = !is_gen_2(device) ? settings.mode : settings.sys?.device?.profile;

	// ignore V-tag RGB bulb, has no modes
	if (device.type === 'S3BL-C010007AEU') return;

	if (isCover(device.type)) {
		// cover mode is always roller
		const secondChannel = get(devices)[id + '_1'];
		if (
			typeof secondChannel !== 'undefined' &&
			secondChannel.category !== 'roller' &&
			device.category === 'roller' &&
			device.mode === 'roller'
		) {
			await executeModeReplacement(device, 'roller', 'category', false);
		}
		if (isShutter(device.type) || isProCover(device.type)) {
			// shutter and cover are always roller
			settings_mode = 'roller';
			// fix missing second channel of isProCover
			// this is a workaround for the case when the second channel is not created
			// ToDo: remove after Release 30
			if (isProCover(device.type) && typeof get(devices)[id + '_1'] === 'undefined') {
				await executeModeReplacement(device, 'roller', 'category', true);
			}
		} else {
			settings_mode = settings.sys?.device?.profile == 'cover' ? 'roller' : 'relay';
		}
	}

	if (typeof settings_mode === 'undefined') return;

	const _device = Object.assign({}, { ...device });
	// do nothing if the mode is correct
	// global check fo matching covers
	if (_device.category === settings_mode && _device.mode === settings_mode) return;

	if (
		(_device.type === 'SHSW-22' || _device?.type === 'SHSW-25') &&
		(_device.category !== settings_mode || (_device?.mode !== settings_mode && !isShared))
	) {
		// roller mode devices
		if (settings_mode === 'relay') {
			await executeModeReplacement(_device, 'relay', 'category', true);
		} else if (settings_mode === 'roller') {
			// Preserve original device name
			if (typeof get(devices)[real_id(id)] !== 'undefined') {
				_device.name = get(devices)[real_id(id)].name; //preventing addition of channel number to name
			}

			await executeModeReplacement(_device, 'roller', 'category', false);
		}
	} else if (
		_device.type === 'SHRGBW2' &&
		typeof _device.mode !== 'undefined' &&
		_device?.mode !== settings_mode
	) {
		// white/color led strip
		if (settings_mode === 'white') {
			await executeModeReplacement(_device, 'white', 'mode', true);
		} else if (settings_mode === 'color') {
			await executeModeReplacement(_device, 'color', 'mode', false);
		}
	} else if (isCover(_device.type) && !isShared) {
		if (settings_mode === 'relay') {
			await executeModeReplacement(_device, 'relay', 'category', true);
		} else if (settings_mode === 'roller') {
			// Preserve original device name
			if (typeof get(devices)[real_id(id)] !== 'undefined') {
				_device.name = get(devices)[real_id(id)].name; //preventing addition of channel number to name
			}

			if (!isProCover(_device.type) && !isShutter(_device.type)) {
				// skip this for ProCover (multiple channeled covers)

				await executeModeReplacement(_device, 'roller', 'category', false);
			} else {
				// For ProCover and Shutter, just get status and settings without mode change
				await getStatus(_device);
				await getSettings(_device);
			}
		}
		if (!initialLoad) await deleteDeviceFromGroupOnModeChange(_device);
	} else if (isProThreeEM(_device.type) && _device?.mode !== settings_mode) {
		if (settings_mode === 'monophase') {
			await executeModeReplacement(_device, 'monophase', 'mode', true);
		} else if (settings_mode === 'triphase') {
			await executeModeReplacement(_device, 'triphase', 'mode', false);
		}
	} else if (
		_device.type === 'SNDC-0D4P10WW' && // Plus RGBW
		_device?.mode !== settings_mode
	) {
		//this will support local mode change
		if (settings_mode !== 'light') {
			await executeModeReplacement(_device, settings_mode, 'mode', false);
		} else {
			await executeModeReplacement(_device, 'light', 'mode', true);
		}
	} else if (isRgbwwPm(device.type) && device?.rgbww_profile !== settings_mode) {
		await executeModeReplacement(_device, settings_mode, 'mode', true);
	}
}

/**
 * Function to get device(channel) mode for Pro RGBWW PM based on device profile
 * @param mode Device profile
 * @param channel Device channel
 * @returns Channel mode
 */
const getProRGBWWDeviceMode = (mode: string, channel: number) => {
	switch (mode) {
		case 'rgbcct':
			return channel === 0 ? 'rgb' : 'cct';
		case 'cctx2':
			return 'cct';
		case 'rgbx2light':
			return channel === 0 ? 'rgb' : 'light';
		case 'light':
		default:
			return 'light';
	}
};

//#endregion Mode switch logic
